// The one REST origin the whole shell dials (docs/04 §2).
//
// The signed endpoint list used to protect only the WebSocket: REST went to the
// build-time origin with no failover, so a carrier that freezes new TLS flows to
// the primary host left this app connected — live socket, live presence — with
// every REST call dying on its own timeout. AUTH is the worst case of it: the
// access token is minted over REST, so an auth origin pinned to the blocked host
// fails every other request before a byte is sent, takes the signaling race down
// with it (the race needs a token), and silences the `/endpoints` poll that is
// the only channel through which a blocked client ever learns about a mirror.
//
// One router per PROCESS, not per session: `accountContext` builds the auth
// client BEFORE any authenticated session exists, and the APK/endpoint poll
// outlives every session. The memory that survives a restart is the persisted
// `{list, lastGoodUrl}` this module seeds itself from.

import {
  OriginRouter,
  type EndpointRouteState,
  type RestOrigin,
} from '@merkury/brain/api/client.js';
import type { KvStore } from '@merkury/brain/storage/kv.js';
import { ENDPOINTS_STATE_KEY } from '@merkury/brain/storage/kvKeys.js';
import { API_BASE_URL } from './config.js';
import { devEndpointList } from './devServer.js';

/**
 * The page's own REST base spelled absolutely, or null when this build has an
 * absolute API base (the APK, whose WebView origin is `https://localhost`) and
 * therefore no same-origin case at all.
 */
function sameOriginRestBase(): string | null {
  if (!API_BASE_URL.startsWith('/')) return null;
  try {
    return `${location.origin}${API_BASE_URL}`;
  } catch {
    return null;
  }
}

/** A `lastGoodUrl` that is PRESENT (so it overwrites the router's winner) and
 *  derives no base (`restBaseFromWsUrl` rejects it) — i.e. "no winner". */
const NO_WINNER = '';

/** Runtime shape check on the untrusted KV value the seed reads back. */
function hasEndpoints(list: unknown): list is { endpoints: readonly { url: string }[] } {
  return (
    typeof list === 'object' &&
    list !== null &&
    Array.isArray((list as { endpoints?: unknown }).endpoints)
  );
}

/**
 * The winner, but only while the CURRENT list still names it —
 * `signaling/client.ts orderedUrls()` requires exactly this membership before
 * preferring it. A winner the list no longer carries is an endpoint the operator
 * revoked (or a production host left over from before a dev-server switch): REST
 * must not take the session's bearer token there, and one authored error body
 * from such a host would mark it `proven` and pin it for good.
 *
 * Cleared rather than omitted: `OriginRouter.noteEndpointState` overwrites its
 * winner only when the field is PRESENT, so omitting it would leave the revoked
 * host at the head of the order — which is also why "the race came home" resets
 * properly here (the own-origin winner is a real winner, see below).
 */
function vetted(state: EndpointRouteState): EndpointRouteState {
  const list = state.list;
  if (!list || state.lastGoodUrl === undefined) return state;
  const named = list.endpoints.some((endpoint) => endpoint.url === state.lastGoodUrl);
  return { list, lastGoodUrl: named ? state.lastGoodUrl : NO_WINNER };
}

/**
 * Shell-wide REST origin.
 *
 * Two spellings, one server: a relative API base (`/api/v1`, the same-origin web
 * default) and the bootstrap list's `wss://<serving host>/ws` name the very same
 * host. The router below reasons in ABSOLUTE bases so its ordering, dedup and
 * rotation see one origin instead of two — including the case where the race
 * moves back home from a mirror, which must put REST back on the page's own
 * origin. `base()` hands the RELATIVE spelling back out, because that is what
 * the page is served and service-worked under and what a plain web install must
 * keep dialling byte-for-byte. An absolute-base build (the APK) maps nothing.
 */
class ShellRestOrigin implements RestOrigin {
  private readonly own: string | null;
  private readonly router: OriginRouter;

  constructor() {
    this.own = sameOriginRestBase();
    // No log sink, deliberately: an endpoint may be named by ADDRESS (protocol
    // `kind:'ip'`) and "no IPs in logs" admits no exception (CLAUDE.md), so
    // nothing in this module can reach `hostOfBase`.
    this.router = new OriginRouter({ configured: this.own ?? API_BASE_URL });
  }

  base(): string {
    const base = this.router.base();
    return base === this.own ? API_BASE_URL : base;
  }

  noteTransportFailure(base: string): void {
    this.router.noteTransportFailure(this.internal(base));
  }

  noteResponse(base: string, authored: boolean): void {
    this.router.noteResponse(this.internal(base), authored);
  }

  noteEndpointState(state: EndpointRouteState): void {
    this.router.noteEndpointState(vetted(state));
  }

  /** Callers report outcomes with the base `base()` gave them. */
  private internal(base: string): string {
    return this.own !== null && base === API_BASE_URL ? this.own : base;
  }
}

let shared: ShellRestOrigin | null = null;
/** True once a caller that knows the EFFECTIVE list has spoken. */
let adopted = false;
let seeding: Promise<void> | null = null;

function router(): ShellRestOrigin {
  shared ??= new ShellRestOrigin();
  return shared;
}

/**
 * Where the next REST/auth byte goes. Safe to call at any point in boot: it
 * builds the router off the build-time base alone — no storage, no await — so a
 * caller that runs before any endpoint list has been adopted simply behaves as
 * this app always did, and re-points as soon as one is.
 */
export function shellRestOrigin(): RestOrigin {
  return router();
}

/** Adopt a list and/or race winner (bring-up, and every signaling `persist`). */
export function noteShellEndpointState(state: EndpointRouteState): void {
  adopted = true;
  router().noteEndpointState(state);
}

/**
 * Point the router at what the LAST session proved, before the first auth byte
 * goes out: on a link where the primary origin is frozen rather than refused,
 * login would otherwise burn its whole token budget on a host this device has
 * already learned to avoid. Single-flight, and it NEVER rejects — a KV that
 * cannot be read must cost the configured base, not the boot.
 */
export function seedShellRestOrigin(kv: KvStore): Promise<void> {
  seeding ??= (async (): Promise<void> => {
    try {
      const stored = (await kv.get<EndpointRouteState>(ENDPOINTS_STATE_KEY)) ?? {};
      // A session that composed while this read was in flight knows the
      // effective list (dev override, bootstrap floor); its note outranks this
      // cold one.
      if (adopted) return;
      // A dev-mode backend override replaces the list outright, so a winner
      // persisted against production is not in it and gets vetted away.
      const list = devEndpointList() ?? (hasEndpoints(stored.list) ? stored.list : undefined);
      // No list, no winner: a `lastGoodUrl` with nothing to check it against is
      // exactly the unvettable case `vetted` refuses to trust.
      if (!list) return;
      const lastGoodUrl = typeof stored.lastGoodUrl === 'string' ? stored.lastGoodUrl : undefined;
      noteShellEndpointState({ list, lastGoodUrl });
    } catch {
      // Storage that cannot be read — or a persisted list too corrupt to walk —
      // costs the configured base, never the boot: this runs in front of the
      // first auth call, and a throw here would strand the app with no token.
    }
  })();
  return seeding;
}

/** Test seam: process-wide state must not leak between tests. */
export function resetShellRestOriginForTest(): void {
  shared = null;
  adopted = false;
  seeding = null;
}
